import React, { useState, useEffect } from 'react';
import { logger } from '../services/logger.ts';
import { ArrowLeft, Save, Loader, Clock, Shield, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/db.ts';
import { useAuth } from '../lib/auth.tsx';
import { useSettings } from '../lib/settings/settingsContext.tsx';
import { usePromoConfig } from '../hooks/usePromoConfig.ts';

export function SystemSettings() {
  const { user } = useAuth();
  const { settings, refreshSettings } = useSettings();
  const { promoConfig } = usePromoConfig();
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showLeaderboard, setShowLeaderboard] = useState<boolean>(false);
  const [currencyForm, setCurrencyForm] = useState({
    code: settings.currency.code,
    symbol: settings.currency.symbol,
    name: settings.currency.name,
    decimalPlaces: settings.currency.decimalPlaces,
    symbolPosition: settings.currency.symbolPosition,
  });

  const [serviceFeeForm, setServiceFeeForm] = useState({
    percentage: settings.serviceFee * 100, // Convert to percentage for display
  });
  
  const [affiliateFeeForm, setAffiliateFeeForm] = useState({
    percentage: settings.affiliateFeePercentage * 100, // Convert to percentage for display
  });
  
  const [creatorFeeForm, setCreatorFeeForm] = useState({
    percentage: settings.creatorFeePercentage * 100, // Convert to percentage for display
  });

  const [timeoutForm, setTimeoutForm] = useState({
    enabled: true,
    inactivityTimeout: 20,
    warningTime: 3,
    financialOpsTimeout: 10,
  });

  // Check if user is admin or superuser
  const isAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';
  
  // Load promo config leaderboard setting
  useEffect(() => {
    if (promoConfig) {
      setShowLeaderboard(promoConfig.show_leaderboard ?? false);
    }
  }, [promoConfig]);
  
  // Load timeout settings
  useEffect(() => {
    const loadTimeoutSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('key, value')
          .in('key', [
            'session_timeout_enabled',
            'inactivity_timeout_minutes',
            'warning_time_minutes',
            'financial_ops_timeout_minutes',
          ]);

        if (!error && data) {
          interface TimeoutSettings {
            enabled?: boolean;
            inactivityTimeout?: number;
            warningTime?: number;
            financialOpsTimeout?: number;
          }
          const timeoutSettings: TimeoutSettings = {};
          data.forEach((row: { key: string; value: any }) => {
            // Extract value from JSONB
            const value = typeof row.value === 'string' ? row.value : String(row.value);
            
            if (row.key === 'session_timeout_enabled') {
              timeoutSettings.enabled = value === 'true' || value === true;
            } else if (row.key === 'inactivity_timeout_minutes') {
              timeoutSettings.inactivityTimeout = parseInt(value, 10);
            } else if (row.key === 'warning_time_minutes') {
              timeoutSettings.warningTime = parseInt(value, 10);
            } else if (row.key === 'financial_ops_timeout_minutes') {
              timeoutSettings.financialOpsTimeout = parseInt(value, 10);
            }
          });

          setTimeoutForm(prev => ({
            ...prev,
            ...timeoutSettings,
          }));
        }
      } catch (err) {
        logger.error('Error loading timeout settings:', err);
      }
    };

    loadTimeoutSettings();
  }, []);

  // Update form when settings change
  useEffect(() => {
    setCurrencyForm({
      code: settings.currency.code,
      symbol: settings.currency.symbol,
      name: settings.currency.name,
      decimalPlaces: settings.currency.decimalPlaces,
      symbolPosition: settings.currency.symbolPosition,
    });
    setServiceFeeForm({
      percentage: settings.serviceFee * 100,
    });
    setAffiliateFeeForm({
      percentage: settings.affiliateFeePercentage * 100,
    });
    setCreatorFeeForm({
      percentage: settings.creatorFeePercentage * 100,
    });
  }, [settings]);
  
  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCurrencyForm(prev => ({
      ...prev,
      [name]: name === 'decimalPlaces' ? parseInt(value, 10) : value,
    }));
    
    // Clear messages
    setError(null);
    setSuccess(null);
  };
  
  // Handle service fee input changes
  const handleServiceFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setServiceFeeForm(prev => ({
      ...prev,
      percentage: parseFloat(value) || 0
    }));
    
    // Clear messages
    setError(null);
    setSuccess(null);
  };
  
  // Handle affiliate fee input changes
  const handleAffiliateFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setAffiliateFeeForm(prev => ({
      ...prev,
      percentage: parseFloat(value) || 0
    }));
    
    // Clear messages
    setError(null);
    setSuccess(null);
  };
  
  // Handle creator fee input changes
  const handleCreatorFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCreatorFeeForm(prev => ({
      ...prev,
      percentage: parseFloat(value) || 0
    }));
    
    // Clear messages
    setError(null);
    setSuccess(null);
  };

  // Handle timeout setting changes
  const handleTimeoutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setTimeoutForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : parseInt(value, 10),
    }));
    
    // Clear messages
    setError(null);
    setSuccess(null);
  };
  
  // Save settings
  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAdmin) {
      setError('You do not have permission to update system settings');
      return;
    }
    
    try {
      setSaveLoading(true);
      setError(null);
      setSuccess(null);
      
      // Convert to database format
      const currencySettings = {
        code: currencyForm.code,
        symbol: currencyForm.symbol,
        name: currencyForm.name,
        decimal_places: currencyForm.decimalPlaces,
        symbol_position: currencyForm.symbolPosition,
      };
      
      // Validate service fee percentage
      if (serviceFeeForm.percentage < 0 || serviceFeeForm.percentage > 20) {
        throw new Error('Service fee must be between 0% and 20%');
      }

      // Validate affiliate fee percentage
      if (affiliateFeeForm.percentage < 0 || affiliateFeeForm.percentage > 100) {
        throw new Error('Affiliate fee must be between 0% and 100% of service fee');
      }

      // Validate creator fee percentage
      if (creatorFeeForm.percentage < 0 || creatorFeeForm.percentage > 100) {
        throw new Error('Creator fee must be between 0% and 100% of service fee');
      }

      // Validate total fees don't exceed service fee
      const totalFeePercentage = affiliateFeeForm.percentage + creatorFeeForm.percentage;
      if (totalFeePercentage > 100) {
        throw new Error('Total affiliate and creator fees cannot exceed 100% of service fee');
      }

      // Update currency settings in database
      const { error: currencyError } = await supabase.rpc('update_setting', {
        setting_key: 'currency',
        setting_value: currencySettings,
        setting_description: 'Currency configuration for the application'
      });
      
      if (currencyError) throw currencyError;

      // Update service fee setting in database
      const { error: serviceFeeError } = await supabase.rpc('update_setting', {
        setting_key: 'service_fee',
        setting_value: serviceFeeForm.percentage / 100, // Send as number, Supabase will convert to JSONB
        setting_description: 'Platform service fee percentage'
      });
      
      if (serviceFeeError) throw serviceFeeError;

      // Update affiliate fee percentage setting in database
      const { error: affiliateFeeError } = await supabase.rpc('update_setting', {
        setting_key: 'affiliate_fee_percentage',
        setting_value: affiliateFeeForm.percentage / 100, // Send as number, Supabase will convert to JSONB
        setting_description: 'Affiliate commission as percentage of service fee'
      });
      
      if (affiliateFeeError) throw affiliateFeeError;

      // Update creator fee percentage setting in database
      const { error: creatorFeeError } = await supabase.rpc('update_setting', {
        setting_key: 'creator_fee_percentage',
        setting_value: creatorFeeForm.percentage / 100, // Send as number, Supabase will convert to JSONB
        setting_description: 'Market creator fee as percentage of service fee'
      });
      
      if (creatorFeeError) throw creatorFeeError;

      // Update timeout settings using direct upsert
      const timeoutSettingsToUpdate = [
        { key: 'session_timeout_enabled', value: timeoutForm.enabled.toString(), description: 'Enable/disable session timeout' },
        { key: 'inactivity_timeout_minutes', value: timeoutForm.inactivityTimeout.toString(), description: 'Minutes of inactivity before logout' },
        { key: 'warning_time_minutes', value: timeoutForm.warningTime.toString(), description: 'Minutes before timeout to show warning' },
        { key: 'financial_ops_timeout_minutes', value: timeoutForm.financialOpsTimeout.toString(), description: 'Timeout for financial operations pages' },
      ];

      for (const setting of timeoutSettingsToUpdate) {
        const { error: timeoutError } = await supabase
          .from('system_settings')
          .upsert({
            key: setting.key,
            value: setting.value,
            description: setting.description
          }, {
            onConflict: 'key'
          });
        
        if (timeoutError) throw timeoutError;
      }

      // Update promo config leaderboard visibility if promo is active
      if (promoConfig && promoConfig.is_active) {
        const { error: promoError } = await supabase.rpc('update_promo_leaderboard_visibility', {
          p_show_leaderboard: showLeaderboard
        });
        
        if (promoError) throw promoError;
      }
      
      // Refresh settings in context
      await refreshSettings();
      
      setSuccess('All settings updated successfully! Changes will apply immediately.');
    } catch (err) {
      logger.error('Error saving settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to update settings');
    } finally {
      setSaveLoading(false);
    }
  };
  
  // Allow access only if user is admin/superuser
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/admin" className="text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-3xl font-bold">System Settings</h1>
          </div>
          
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            You do not have permission to access system settings. You need to be an admin/superuser or have the 'system_configuration' permission.
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin" className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <h1 className="text-3xl font-bold">System Settings</h1>
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            {success}
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Currency Settings</h2>
          
          <form onSubmit={handleSaveSettings} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency Code
                </label>
                <input
                  type="text"
                  name="code"
                  value={currencyForm.code}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                  placeholder="ZAR"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  The three-letter ISO currency code (e.g., ZAR, USD, EUR)
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency Symbol
                </label>
                <input
                  type="text"
                  name="symbol"
                  value={currencyForm.symbol}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                  placeholder="R"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  The symbol used for currency display (e.g., R, $, €)
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={currencyForm.name}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                  placeholder="South African Rand"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  The full name of the currency
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Decimal Places
                </label>
                <input
                  type="number"
                  name="decimalPlaces"
                  value={currencyForm.decimalPlaces}
                  onChange={handleChange}
                  min="0"
                  max="4"
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Number of decimal places to display (typically 2)
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Symbol Position
                </label>
                <select
                  name="symbolPosition"
                  value={currencyForm.symbolPosition}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                  required
                >
                  <option value="before">Before (e.g., R100.00)</option>
                  <option value="after">After (e.g., 100.00R)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Where to place the currency symbol
                </p>
              </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-gray-200 my-6"></div>
            
            {/* Service Fee Settings */}
            <h2 className="text-xl font-bold mb-4">Service Fee Configuration</h2>
            
            <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Important Notice
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>Changing fee settings will affect:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>All new trades placed after the change</li>
                      <li>Affiliate commissions (percentage of service fee)</li>
                      <li>Market creator fees (percentage of service fee)</li>
                      <li>Platform revenue calculations</li>
                    </ul>
                    <p className="mt-2 font-medium">Existing trades will retain their original fee structure.</p>
                    <p className="mt-1 text-xs">Note: Individual markets can override creator fees. Both affiliate and creator fees are configurable.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Fee Percentage
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={serviceFeeForm.percentage}
                  onChange={handleServiceFeeChange}
                  min="0"
                  max="20"
                  step="0.1"
                  className="w-full rounded-md border border-gray-300 px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                  placeholder="8.0"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-gray-500 text-sm">%</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Platform service fee as a percentage (0-20%). Current: {(settings.serviceFee * 100).toFixed(1)}%
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Affiliate Fee Percentage
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={affiliateFeeForm.percentage}
                  onChange={handleAffiliateFeeChange}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full rounded-md border border-gray-300 px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                  placeholder="12.5"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-gray-500 text-sm">%</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Affiliate commission as percentage of service fee (0-100%). Current: {(settings.affiliateFeePercentage * 100).toFixed(1)}%
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Creator Fee Percentage
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={creatorFeeForm.percentage}
                  onChange={handleCreatorFeeChange}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full rounded-md border border-gray-300 px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                  placeholder="12.5"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-gray-500 text-sm">%</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Market creator fee as percentage of service fee (0-100%). Current: {(settings.creatorFeePercentage * 100).toFixed(1)}%
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Note: Individual markets can override this default value
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-md p-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Fee Breakdown Preview</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <div className="flex justify-between">
                  <span>Service Fee ({serviceFeeForm.percentage.toFixed(1)}%):</span>
                  <span>R{(100 * serviceFeeForm.percentage / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Affiliate Commission ({affiliateFeeForm.percentage.toFixed(1)}% of service fee):</span>
                  <span>R{(100 * serviceFeeForm.percentage / 100 * affiliateFeeForm.percentage / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Creator Commission ({creatorFeeForm.percentage.toFixed(1)}% of service fee):</span>
                  <span>R{(100 * serviceFeeForm.percentage / 100 * creatorFeeForm.percentage / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Platform Revenue:</span>
                  <span>R{(100 * serviceFeeForm.percentage / 100 * (1 - affiliateFeeForm.percentage / 100 - creatorFeeForm.percentage / 100)).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-medium border-t pt-1 mt-1">
                  <span>Amount After Fee:</span>
                  <span>R{(100 * (1 - serviceFeeForm.percentage / 100)).toFixed(2)}</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Based on a R100 trade amount
              </p>
            </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-gray-200 my-6"></div>
            
            {/* Session Timeout Settings */}
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Security & Session Timeout
            </h2>
            
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Clock className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Session Timeout Protection
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>Session timeouts enhance security by:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>Protecting accounts when users step away from their devices</li>
                        <li>Meeting regulatory compliance for betting platforms</li>
                        <li>Promoting responsible gambling practices</li>
                        <li>Preventing unauthorized access to financial operations</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Enable Session Timeout
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Automatically log out users after inactivity
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="enabled"
                    checked={timeoutForm.enabled}
                    onChange={handleTimeoutChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Inactivity Timeout (minutes)
                </label>
                <input
                  type="number"
                  name="inactivityTimeout"
                  value={timeoutForm.inactivityTimeout}
                  onChange={handleTimeoutChange}
                  min="5"
                  max="120"
                  disabled={!timeoutForm.enabled}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  User will be logged out after this many minutes of inactivity (5-120 minutes). Recommended: 20 minutes.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Warning Time (minutes)
                </label>
                <input
                  type="number"
                  name="warningTime"
                  value={timeoutForm.warningTime}
                  onChange={handleTimeoutChange}
                  min="1"
                  max="10"
                  disabled={!timeoutForm.enabled}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Show warning modal this many minutes before timeout (1-10 minutes). Recommended: 3 minutes.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Financial Operations Timeout (minutes)
                </label>
                <input
                  type="number"
                  name="financialOpsTimeout"
                  value={timeoutForm.financialOpsTimeout}
                  onChange={handleTimeoutChange}
                  min="5"
                  max="60"
                  disabled={!timeoutForm.enabled}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Stricter timeout for wallet/deposit/withdrawal pages (5-60 minutes). Recommended: 10 minutes.
                </p>
              </div>

              <div className="bg-gray-50 rounded-md p-3">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Current Timeout Configuration</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Status:</span>
                    <span className={timeoutForm.enabled ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                      {timeoutForm.enabled ? '✓ Enabled' : '✗ Disabled'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Regular browsing timeout:</span>
                    <span>{timeoutForm.inactivityTimeout} minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Financial pages timeout:</span>
                    <span>{timeoutForm.financialOpsTimeout} minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Warning shown at:</span>
                    <span>{timeoutForm.warningTime} minutes before logout</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-gray-200 my-6"></div>
            
            {/* Promo Configuration */}
            {promoConfig && (
              <>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5" />
                  Promo Configuration
                </h2>
                
                <div className="space-y-4">
                  <div className="bg-wayja-teal/10 border border-wayja-teal/30 rounded-md p-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <Trophy className="h-5 w-5 text-wayja-teal" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-wayja-dark-teal">
                          Current Promo: {promoConfig.promo_name}
                        </h3>
                        <div className="mt-2 text-sm text-gray-600">
                          <p>Status: <span className={promoConfig.is_active ? 'text-green-600 font-medium' : 'text-gray-600'}>
                            {promoConfig.is_active ? 'Active' : 'Inactive'}
                          </span></p>
                          {promoConfig.start_date && promoConfig.end_date && (
                            <p className="mt-1">
                              Period: {new Date(promoConfig.start_date).toLocaleDateString()} - {new Date(promoConfig.end_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Show Leaderboard to Users
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Enable or disable leaderboard visibility on the dashboard. Leaderboard is collapsed by default when visible.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showLeaderboard}
                        onChange={(e) => setShowLeaderboard(e.target.checked)}
                        disabled={!promoConfig.is_active}
                        className="sr-only peer"
                      />
                      <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${!promoConfig.is_active ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                    </label>
                  </div>

                  {!promoConfig.is_active && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                      <p className="text-xs text-yellow-800">
                        ⚠️ Leaderboard visibility can only be changed when a promo is active.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
            
            {!promoConfig && (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-6">
                <p className="text-sm text-gray-600">
                  No active promo configuration found. Promo settings will appear here when a promo is active.
                </p>
              </div>
            )}
            
            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={saveLoading}
                className="px-6 py-2.5 bg-wayja-teal text-white rounded-lg hover:bg-wayja-dark-teal disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {saveLoading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save All Settings
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 